import _uniq from "lodash/uniq";
import _compact from "lodash/compact";
import { createContext } from 'react';
import { hashQueryKey, useQueries } from '@tanstack/react-query';
import { queryClient as appQueryClient } from 'app/queryClient';
import createBatchLookup from 'app/util/lookups/createBatchLookup';
export const hasId = id => id != null && String(id) !== '';
export const uniqueIds = ids => _uniq(ids.filter(hasId).map(String));
export const lookupQueryKey = (name, id) => [name, String(id)];

/** batched lookup, cached per record by key; hooks for components, imperative API for everything else */
export default function createLookup(name, fetchBatch, {
  getKey = record => record.id,
  onReceive,
  batchSize,
  batchDelay,
  staleTime = 30000,
  cacheTime,
  queryClient = appQueryClient
} = {}) {
  const batchLoader = createBatchLookup(ids => fetchBatch(ids).then(records => {
    onReceive?.(records);
    return records;
  }), getKey, {
    batchSize,
    batchDelay
  });
  if (cacheTime !== undefined) {
    queryClient.setQueryDefaults([name], {
      cacheTime
    });
  }

  // works outside QueryClientProvider (e.g. popovers)
  const context = /*#__PURE__*/createContext(queryClient);
  const queryKey = id => lookupQueryKey(name, id);
  const queryOptions = id => ({
    queryKey: queryKey(id),
    queryFn: () => batchLoader.load(id),
    staleTime,
    retry: 1 // only failed requests; missing records resolve null
  });
  const useLookupMany = ids => {
    const results = useQueries({
      queries: uniqueIds(ids).map(id => queryOptions(id)),
      context
    });
    const isLoaded = results.every(({
      data
    }) => data !== undefined);
    return {
      data: isLoaded ? _compact(results.map(({
        data
      }) => data)) : undefined,
      isLoading: results.some(({
        isLoading
      }) => isLoading),
      error: results.find(({
        error
      }) => error)?.error ?? null
    };
  };
  const useLookup = id => {
    const {
      data,
      ...result
    } = useLookupMany([id]);
    if (!hasId(id) || !data) {
      return {
        ...result,
        data: undefined
      };
    }
    return {
      ...result,
      data: data[0] ?? null
    };
  };

  /** fetches if uncached or stale; calls are batched */
  const lookup = id => hasId(id) ? queryClient.fetchQuery(queryOptions(id)) : Promise.resolve(null);

  /** found records only, deduped, in input order */
  const lookupMany = ids => Promise.all(uniqueIds(ids).map(lookup)).then(_compact);

  /** cache read, never fetches: undefined if not loaded, null if not found */
  const get = id => hasId(id) ? queryClient.getQueryCache().get(hashQueryKey(queryKey(id)))?.state.data : undefined;

  /** seeds records fetched elsewhere; skips cached ones so late responses can't overwrite them */
  const ingest = records => {
    records.forEach(record => {
      const key = getKey(record);
      if (get(key) === undefined) {
        queryClient.setQueryData(queryKey(key), record);
      }
    });
    onReceive?.(records);
  };

  /** marks records stale (all if no ids); they refetch on next use */
  const invalidate = ids => {
    if (!Array.isArray(ids)) {
      return queryClient.invalidateQueries({
        queryKey: hasId(ids) ? queryKey(ids) : [name]
      });
    }

    // one cache pass, not one per id
    const keys = new Set(ids.filter(hasId).map(String));
    return queryClient.invalidateQueries({
      queryKey: [name],
      predicate: ({
        queryKey: [, id]
      }) => keys.has(String(id))
    });
  };

  /** drops all records; rendered ones refetch */
  const clear = () => queryClient.resetQueries({
    queryKey: [name]
  });

  /** cancels fetches and drops all records without refetching (e.g. logout) */
  const remove = () => {
    queryClient.cancelQueries({
      queryKey: [name]
    });
    queryClient.removeQueries({
      queryKey: [name]
    });
  };
  return {
    useLookup,
    useLookupMany,
    lookup,
    lookupMany,
    get,
    ingest,
    invalidate,
    clear,
    remove,
    queryKey
  };
}